import { useState, type FormEvent } from "react";
import { useLocation, useNavigate, type Location } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../api/client";
import { login } from "../api/auth";

// Shape RequireAuth attaches to the /login redirect's history state — see
// App.tsx's RequireAuth. Optional/loose on purpose: a direct visit to
// /login (no redirect) carries no state at all.
interface LoginLocationState {
  from?: Pick<Location, "pathname" | "search">;
}

export function LoginPage() {
  const { api, setSession } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = (location.state as LoginLocationState | null)?.from;
  const target = from ? `${from.pathname}${from.search}` : "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await login(api, email, password);
      if ("requires_2fa" in res) {
        sessionStorage.setItem("prism-temp-2fa", JSON.stringify({ temp_token: res.temp_token, email }));
        // Carry `from` through the 2FA hop so a signed-out visit to a deep
        // link like /delete-account still lands there post-2FA, not on "/".
        nav("/2fa/challenge", { replace: true, state: { from } });
      } else {
        setSession({ access_token: res.access_token, refresh_token: res.refresh_token, account: res.account });
        nav(target, { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card center-card">
      <h1>Sign in</h1>
      <p className="meta">Prism account</p>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" type="email" value={email} autoFocus required
            onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input id="password" type="password" value={password} required
            onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <div className="error">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
